import { createApp } from 'vue';
import * as BVN from 'bootstrap-vue-next';
import userManager, { setGlobalProperties } from './services/SecurityService';

import './vendor.js';

import App from './App.vue';
import router from './router';
import { createPinia } from 'pinia';

import upperFirst from 'lodash/upperFirst';
import camelCase from 'lodash/camelCase';

// Toast replacement (vue-toasted → vue-toastification)
import Toast, { useToast } from 'vue-toastification';
import 'vue-toastification/dist/index.css';

// vue-currency-input v3 no longer has a global plugin. Use per-component `useCurrencyInput()` composable instead.
import fileReader from "@/services/fileReader.js";

import signalR from './services/SignalR';

import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';

import axios from 'axios';

import { Form as VeeForm, Field as VeeField, defineRule, configure } from 'vee-validate';
import * as allRules from '@vee-validate/rules';

import { numberFormatter } from './components/Common/Formatters.js';

import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

import moment from 'moment';

import BootstrapIcons from './components/BootstrapIcons.js';
import { setupCalendar } from 'v-calendar';
import bvModalShim from './plugins/bvModalShim.js';

// Defer ResizeObserver callbacks by one animation frame so BVN dropdown/modal
// positioning can't feed back into itself and trigger the benign-but-noisy
// "ResizeObserver loop" error. Also guard the dev-server overlay as a fallback.
if (typeof window !== 'undefined' && window.ResizeObserver) {
    const NativeResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class DeferredResizeObserver extends NativeResizeObserver {
        constructor(callback) {
            super((entries, observer) => {
                window.requestAnimationFrame(() => {
                    try { callback(entries, observer); } catch (_) { /* swallow */ }
                });
            });
        }
    };
}
const RESIZE_OBSERVER_ERR = /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/;
const swallowResizeErr = e => {
    const msg = e?.message || e?.reason?.message;
    if (msg && RESIZE_OBSERVER_ERR.test(msg)) {
        e.stopImmediatePropagation?.();
        e.preventDefault?.();
        return true;
    }
    return false;
};
window.addEventListener('error', swallowResizeErr, true);
window.addEventListener('unhandledrejection', swallowResizeErr, true);

// Hide bootstrap-icons glyphs until the webfont is actually loaded. Otherwise a
// hard refresh shows tofu boxes while the browser renders ::before content in a
// fallback font that has no PUA codepoints.
if (document?.fonts?.load) {
    document.documentElement.classList.add('bi-loading');
    const reveal = () => document.documentElement.classList.remove('bi-loading');
    document.fonts.load('1em "bootstrap-icons"').then(reveal, reveal);
}

// ── Create app ──────────────────────────────────────────────
const app = createApp(App);

// Wire up globalProperties reference for SecurityService (replaces Vue.prototype access)
setGlobalProperties(app.config.globalProperties);

// ── Plugins ─────────────────────────────────────────────────
// Pinia must mount before any module that resolves a store via useXxxStore().
app.use(createPinia());
app.use(router);

app.use(BVN.createBootstrap({ components: true, directives: true }));
app.use(setupCalendar, {});
app.use(bvModalShim);

// Manually register all BVN components globally (createBootstrap doesn't always do this)
Object.entries(BVN).forEach(([name, component]) => {
    if (name.startsWith('B') && name !== 'BvEvent' && typeof component === 'object' && component.setup) {
        app.component(name, component);
    }
});

// Manually register BVN directives globally
app.directive('b-toggle', BVN.vBToggle);
app.directive('b-tooltip', BVN.vBTooltip);
app.directive('b-modal', BVN.vBModal);
app.directive('b-popover', BVN.vBPopover);

// Template convenience for the migrated FeatureFlags Pinia store, so existing
// `v-if="$store.getters.featureFlagEnabled('X')"` template usages can drop
// `$store.getters.` and become `$featureFlagEnabled('X')` without each
// component needing to import the store.
import { useFeatureFlagsStore } from '@/stores/featureFlags.js';
app.config.globalProperties.$featureFlagEnabled = flag => useFeatureFlagsStore().featureFlagEnabled(flag);

import { useCurrentUserStore } from '@/stores/currentUser.js';
app.config.globalProperties.$currentUserHasRole = role => useCurrentUserStore().currentUserHasRole(role);

import { useAppStateStore } from '@/stores/appState.js';
import { getExportMessage } from '@/components/Common/ExportMessage.js';

// Layout body-class plumbing — replaces the old Vuex SettingPlugin. Subscribes
// to setting flag changes and toggles the matching CSS class on <body>.
import { installSettingBodyClasses } from '@/stores/setting.js';
installSettingBodyClasses();

// Appearance preference. The inline script in public/index.html has already stamped the
// cached theme before this bundle loaded (that is what prevents a white flash on every
// page load), so the store's only job here is to reconcile against the server once the
// signed-in user is known — see the router guard below.
import { useThemeStore } from '@/stores/theme.js';

// Printing is always light, in every theme. Our dark layer is scoped to @media screen so it
// cannot reach print at all; this covers Bootstrap's own dark mode, which ships in
// chunk-vendors.css and declares some of its variables on components rather than on :root --
// a depth no CSS reset of ours can outrank. See installPrintThemeGuard.
//
// Installed once at boot rather than per-user: it is about the output medium, not about who
// is signed in, and it is a no-op whenever the attribute is already light.
import { installPrintThemeGuard } from '@/services/themeService.js';
installPrintThemeGuard();

app.use(BootstrapIcons);

app.use(Toast, {
    position: 'top-center',
    pauseOnHover: true,
    containerClassName: 'toast-container'
});

app.use(signalR, { baseURL: process.env.VUE_APP_API_ROOT_URL });

// ── Toast helpers (replaces Vue.toasted.global.app_error etc.) ──
const toast = useToast();

function appError(payload) {
    const options = { timeout: false };

    if (!payload) {
        toast.error('Oops... Something went wrong.', options);
        return;
    }

    if (typeof payload === 'string') {
        toast.error(payload, options);
        return;
    }

    if (payload.correlationId) {
        let stackTrace = '';
        if (payload.stackTrace) {
            stackTrace = ` ${payload.stackTrace}`;
        }
        toast.error(`${payload.message} ${stackTrace} \n\nError ID: ${payload.correlationId}`, options);
        return;
    }

    let msg = '';
    payload = payload.errors || payload;
    for (let key in payload) {
        if (Object.prototype.hasOwnProperty.call(payload, key)) {
            for (let i = 0; i < payload[key].length; i++) {
                msg += payload[key][i] + '\n';
            }
        }
    }

    if (msg.length === 0) {
        msg = 'Oops... Something went wrong.';
    }

    toast.error(msg, options);
}

function appSuccess(payload) {
    toast.success(payload || 'Success!');
}

function appWarning(payload) {
    toast.warning(payload || 'An informational message was not provided. Select CLOSE to continue.');
}

// Make toast helpers available globally
app.config.globalProperties.$toast = toast;
app.config.globalProperties.$appError = appError;
app.config.globalProperties.$appSuccess = appSuccess;
app.config.globalProperties.$appWarning = appWarning;

// Export for use outside components (e.g. axios interceptors, router guards)
export { appError, appSuccess, appWarning };

// ── Router guards ───────────────────────────────────────────
router.beforeEach(async (to) => {
    const allowAnonymous = to.matched.some(
        record => record.meta.allowAnonymous
    );

    if (allowAnonymous) {
        return true;
    }

    const user = await userManager.getUser();
    if (user && !user.expired) {
        const currentUserStore = useCurrentUserStore();
        if (currentUserStore.currentUserHasRole('Cashier')
            && !Array.isArray(user.profile.role)
            && to.path !== '/Error/Unauthorized'
            && to.path !== '/logout') {
                return '/Error/Unauthorized';
        }

        if (currentUserStore.currentUserHasRole('Unassigned')
            && !Array.isArray(user.profile.role)
            && to.path !== '/home'
            && to.path !== '/logout') {
                appWarning('Currently no roles are assigned to your user account and you will not be able to navigate Mosaic.');
                return '/home';
        }

        if (currentUserStore.getCurrentUser === null) {
            currentUserStore.setCurrentUser(user);
            // Once per session, on the first authenticated navigation. Deliberately not
            // awaited: a preference must never delay a route, and hydrate() swallows its
            // own failures.
            useThemeStore().hydrate(currentUserStore.getCurrentUserId);
        }

        // Resolve the store time zone before the first screen mounts so
        // date/time formatters never race the request (DV: Data Exchange Log
        // Last Run Date showed in the browser zone instead of the store zone).
        await selectListOptionsDataContext.ensureStoreTimeZoneLoadedAsync();

        app.config.globalProperties.startSignalR();

        const isLocationFiltered = to.matched.some(record => record.meta.singleLocationFilter);
        const isWebOrderFulfillmentLocationFiltered = to.matched.some(record => record.meta.webOrderFulfillmentLocation);
        if (isLocationFiltered || isWebOrderFulfillmentLocationFiltered) {
            const appStateStore = useAppStateStore();
            if (isLocationFiltered && appStateStore.getSingleLocationId === null) {
                const resp = await axios.get('selectlists/singlelocationid');
                appStateStore.updateSingleLocationId(resp.data);
            }
            if (isWebOrderFulfillmentLocationFiltered && appStateStore.getWebOrderFulfillmentLocationIds.length === 0) {
                const resp = await axios.get('storesetup/weborderfulfillmentlocationids');
                appStateStore.updateWebOrderFulfillmentLocationIds(resp.data);
            }

            let userId = useCurrentUserStore().getCurrentUserId;
            let localStorageLocationId = localStorage.getItem('SelectedLocationId' + userId);
            let selectedLocationId = localStorageLocationId ?? await selectListOptionsDataContext.getSelectedLocationIdAsync();

            if ((isLocationFiltered && selectedLocationId != appStateStore.getSingleLocationId)
                || (isWebOrderFulfillmentLocationFiltered && !appStateStore.getWebOrderFulfillmentLocationIds.includes(Number(selectedLocationId)))) {
                appError('This section of Mosaic is not accessible to this location.');
                return { path: '/', replace: true };
            }
        }

        return true;
    } else {
        app.config.globalProperties.stopSignalR();
        await userManager.clearStaleState();
        try {
            await userManager.signinRedirect({ state: to.path });
        } catch (err) {
            appError('Unable to redirect to sso login: ' + err.message);
        }
        return false;
    }
});

router.afterEach(to => {
    if (to.meta && to.meta.title) {
        document.title = to.meta.title + ' - Mosaic';
    }
});

// ── Axios setup ─────────────────────────────────────────────
axios.defaults.baseURL = process.env.VUE_APP_API_ROOT_URL;

axios.interceptors.request.use(config => {
    return userManager.getUser().then(function(user) {
        if (user) {
            config.headers.authorization = 'Bearer ' + user.access_token;
            return config;
        }

        return null;
    });
});

axios.interceptors.response.use(
    res => {
        // An export returns a file, so anything the api wants to tell the user about it arrives in a
        // header instead of a body. Raising it here means every export gets the toast without its own
        // handling; the caller only has to decide whether to still show its success message.
        const exportMessage = getExportMessage(res);

        if (exportMessage) {
            appWarning(exportMessage);
        }

        return res;
    },
    err => {
        // Opt-out for calls that must never interrupt the user. Absent on every existing
        // request, so nothing else changes behaviour.
        //
        // Added for the appearance preference: it is fetched on first authenticated
        // navigation, and without this a failing endpoint produced a non-dismissing error
        // toast on app boot -- or, on a 403, redirected the user to the Unauthorized page
        // over a theme setting. The caller already treats failure as "keep the cached
        // preference", so the error genuinely has nowhere useful to go.
        if (err.config && err.config.suppressGlobalError) {
            return Promise.reject(err);
        }

        if(err.response)
        {
            switch(err.response.status){
                case 403:
                    router.push('Error/Unauthorized');
                    break;
                default:
                    if(err.config.responseType === 'blob'){

                        fileReader(err.response.data).then(resp => {
                            appError(JSON.parse(resp));
                        });
                    }
                    else{
                        appError(err.response.data);
                    }
            }
        }

        return Promise.reject(err);
    }
);

// ── Vee-validate 4 setup ────────────────────────────────────
// Register all rules
Object.entries(allRules).forEach(([name, rule]) => {
    if (typeof rule === 'function') {
        defineRule(name, rule);
    }
});

configure({
    validateOnInput: true,
    // @vee-validate/rules ships only the validators in v4; the built-in messages
    // moved to @vee-validate/i18n (not installed). Restore the common ones here
    // so failing rules don't all fall back to "<field> is not valid".
    generateMessage: (ctx) => {
        const field = ctx.field;
        const params = ctx.rule?.params ?? [];
        // params can be positional (array, e.g. max:25) or named (object, e.g.
        // max:{length:25}). Normalize to a positional list so messages
        // interpolate the value instead of rendering "[object Object]".
        const paramList = Array.isArray(params)
            ? params
            : (params && typeof params === 'object' ? Object.values(params) : [params]);
        const arg = paramList[0];
        switch (ctx.rule?.name) {
            case 'min_value': return `The ${field} field must be ${arg} or more`;
            case 'max_value': return `The ${field} field must be ${arg} or less`;
            case 'min': return `The ${field} field must be at least ${arg} characters`;
            case 'max': return `The ${field} field may not be greater than ${arg} characters`;
            case 'length': return `The ${field} field must be ${arg} characters`;
            case 'between':
                return `The ${field} field must be between ${arg} and ${paramList[1] ?? ''}`;
            case 'email': return `The ${field} field must be a valid email`;
            case 'numeric': return `The ${field} field may only contain numeric characters`;
            case 'integer': return `The ${field} field must be an integer`;
            case 'alpha': return `The ${field} field may only contain alphabetic characters`;
            case 'alpha_num': return `The ${field} field may only contain alpha-numeric characters`;
            case 'alpha_spaces': return `The ${field} field may only contain alphabetic characters and spaces`;
            case 'alpha_dash': return `The ${field} field may contain alpha-numeric characters as well as dashes and underscores`;
            case 'regex': return `The ${field} field format is invalid`;
            case 'url': return `The ${field} field must be a valid URL`;
            case 'confirmed': return `The ${field} field confirmation does not match`;
            case 'one_of': return `The ${field} field is not a valid value`;
            case 'is': return `The ${field} field does not match`;
            case 'is_not': return `The ${field} field is invalid`;
            case 'digits': return `The ${field} field must be numeric and exactly contain ${arg} digits`;
            case 'size': return `The ${field} field size must be less than ${arg}KB`;
            case 'mimes': return `The ${field} field must have a valid file type`;
            case 'ext': return `The ${field} field is not a valid file`;
            case 'image': return `The ${field} field must be an image`;
            case 'required': return `The ${field} field is required`;
            case 'selectRequired': return `The ${field} field is required`;
            default: return `The ${field} field is not valid`;
        }
    },
});

// Custom rules
defineRule('strong_password', (value) => {
    if (!/[A-Za-z]/.test(value)) {
        return 'Password must be at least 7 characters and contain at least: 1 letter and 1 number';
    }
    if (!/[0-9]/.test(value)) {
        return 'Password must be at least 7 characters and contain at least: 1 letter and 1 number';
    }
    return true;
});

defineRule('valid_username', (value) => {
    if (!/^[A-Za-z0-9@_.+-]+$/.test(value)) {
        return 'User name can only contain alphanumeric characters and @, _, -, + or .';
    }
    return true;
});

// Register vee-validate components globally
app.component('VeeForm', VeeForm);
app.component('VeeField', VeeField);
// Keep old names as aliases for gradual migration
app.component('ValidationProvider', VeeField);
app.component('ValidationObserver', VeeForm);

// ── Bootstrap Vue Next ──────────────────────────────────────
// (registered via createBootstrap() plugin above)

// ── Global filter → global property ─────────────────────────
app.config.globalProperties.$filters = {
    number: numberFormatter
};

// ── Auto-register p-* components ────────────────────────────
const requireComponent = require.context(
    './components',
    false,
    /p-[\w.-]+\.(vue|js)$/
);

requireComponent.keys().forEach(fileName => {
    const componentConfig = requireComponent(fileName);
    const componentName = upperFirst(
        camelCase(
            fileName
                .split('/')
                .pop()
                .replace(/\.\w+$/, '')
        )
    );
    app.component(
        componentName,
        componentConfig.default || componentConfig
    );
});

// ── Global mixin → global properties ────────────────────────
app.config.globalProperties.SECURITYROLES = {
    DEVELOPER: 'Developer',
    SUPPORT: 'Support',
    ADMIN: 'Admin',
    GMBUYER: 'GM Buyer',
    STOREMANAGER: 'Store Manager',
    BUYBACKUSER: 'Buyback User',
    MERCHANDISEMANAGER: 'Merchandise Manager',
    ACCOUNTINGADMIN: 'Accounting Admin'
};

// ── Quill editor ────────────────────────────────────────────
app.component('custom-quill-editor', QuillEditor);

// ── ContentWrapper (used in Demo pages) ─────────────────────
import ContentWrapper from './components/Layout/ContentWrapper.vue';
app.component('ContentWrapper', ContentWrapper);

// ── Date formats ────────────────────────────────────────────
app.config.globalProperties.$validDates = [
    'YYYY-MM-DD HH:mm:ss.SSS',
    // Single-letter M/D tokens accept one or two digits in strict mode, so 10/9/2026 and 10/09/2026 both parse.
    'YYYY/M/D',
    'YYYY.M.D',
    'M-D-YYYY',
    'M/D/YYYY',
    'M.D.YYYY',
    moment.ISO_8601
];

// ── Modal scroll forwarding ─────────────────────────────────
// When a modal is open, forward wheel events on the overlay to the modal
document.addEventListener('wheel', (e) => {
    const modals = document.querySelectorAll('.modal.show');
    if (modals.length === 0) return;
    const openModal = modals[modals.length - 1];

    // Don't hijack wheel events targeted at a dropdown menu — its own scrollable
    // body should consume them. The menu may be rendered outside .modal-dialog
    // due to strategy="fixed" / teleport.
    if (e.target.closest?.('.dropdown-menu')) return;

    const modalDialog = openModal.querySelector('.modal-dialog');
    if (modalDialog && modalDialog.contains(e.target)) return;

    e.preventDefault();
    e.stopPropagation();

    // Normalize deltaY — different browsers/devices send different scales
    const delta = e.deltaMode === 1 ? e.deltaY * 20 : e.deltaY;
    openModal.scrollBy({ top: delta, behavior: 'smooth' });
}, { capture: true, passive: false });

// ── Mount ───────────────────────────────────────────────────
app.mount('#app');
